import type { LegalDocumentId } from "../documents";
import { CONTROLLING_LANGUAGE, resolveDocumentLanguage } from "../documents";

/**
 * Le texte des documents contractuels, embarque dans l'application.
 *
 * POURQUOI PLUS AUCUN FICHIER SERVI
 * ---------------------------------
 * Trois etapes, et la troisieme est la bonne :
 *
 *  1. Lien vers /legal/Terms_of_Service_FR.docx. Fichiers absents du depot,
 *     donc 404 -- et meme presents, un .docx se telecharge au lieu de
 *     s'afficher, dans Word sur poste et souvent nulle part sur mobile.
 *  2. JSON servi depuis public/. Plus de bundle a alourdir, mais le texte
 *     dependait d'un fichier deploye a cote : une page contractuelle qui
 *     repond « document introuvable » est un consentement sans texte.
 *  3. JSON dans src/, charge en import dynamique. Le texte fait partie de
 *     l'application : il ne peut plus manquer au deploiement, il est
 *     versionne avec le code, et un fichier absent devient une erreur de
 *     compilation au lieu d'un 404 chez le client.
 *
 * Le bundle principal n'enfle pas pour autant : chaque document est un import
 * dynamique, donc un fragment separe que webpack ne telecharge qu'a l'ouverture
 * de la page. Les 29 autres ecrans ne paient rien.
 *
 * Les fichiers sont produits par `npm run legal:build` a partir des .docx
 * deposes dans public/legal/. Ne jamais les editer a la main : la prochaine
 * generation les ecraserait, et une correction faite seulement dans le JSON
 * creerait un ecart entre le texte affiche et le document signe -- exactement
 * ce que la preuve ecrite dans `legal_consent` est censee attester.
 */

export type LegalBlock =
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] };

export interface LegalSection {
  /** Numerotation telle qu'elle figure dans le document signe ("1", "5.2"). */
  number?: string;
  heading: string;
  /**
   * Corps de la section, dans l'ordre du document.
   *
   * Un tableau de blocs plutot que `paragraphs` + `bullets` separes : un
   * article alterne souvent texte, liste, texte. Deux champs distincts
   * perdraient cet ordre et afficheraient toutes les listes apres tout le
   * texte.
   */
  blocks?: LegalBlock[];
  children?: LegalSection[];
}

export interface LegalDocumentContent {
  documentId: LegalDocumentId;
  language: string;
  title: string;
  subtitle?: string | null;
  effectiveDate?: string | null;
  publisher: string;
  /** Fichier .docx d'origine, pour retrouver la source en cas de doute. */
  source?: string;
  generatedAt?: string;
  intro: LegalBlock[];
  sections: LegalSection[];
}

/**
 * Langues dans lesquelles les documents existent reellement, dans l'ordre du
 * menu. L'anglais d'abord parce qu'il fait foi, le reste par ordre alphabetique
 * de code.
 *
 * L'estonien est present parce que l'editeur (CodeNekt For Fleet OÜ) est
 * estonien et que le document peut etre reclame dans cette langue, alors meme
 * qu'aucun ecran de l'application n'est traduit en estonien.
 */
export const DOCUMENT_LANGUAGES = ["en", "de", "es", "et", "fr", "it", "pt"] as const;

export type DocumentLanguage = (typeof DOCUMENT_LANGUAGES)[number];

/** Nom de chaque langue, ecrit dans cette langue. */
export const LANGUAGE_LABELS: Record<DocumentLanguage, string> = {
  en: "English",
  de: "Deutsch",
  es: "Español",
  et: "Eesti",
  fr: "Français",
  it: "Italiano",
  pt: "Português",
};

/**
 * Table d'imports dynamiques.
 *
 * Ecrite en clair plutot qu'en `import(\`./${document}.${language}.json\`)` :
 * un chemin construit a l'execution ne peut pas etre verifie par le
 * compilateur, et un fichier manquant ne se verrait qu'en production, sur la
 * page ou l'utilisateur accepte un contrat. Ici, une entree sans fichier est
 * une erreur de compilation.
 *
 * Chaque `import()` produit un fragment separe : le document n'est telecharge
 * qu'a l'ouverture de la page, et seulement dans la langue demandee.
 */
const LOADERS: Record<
  LegalDocumentId,
  Record<DocumentLanguage, () => Promise<{ default: LegalDocumentContent }>>
> = {
  terms: {
    en: () => import("./terms.en.json") as Promise<{ default: LegalDocumentContent }>,
    de: () => import("./terms.de.json") as Promise<{ default: LegalDocumentContent }>,
    es: () => import("./terms.es.json") as Promise<{ default: LegalDocumentContent }>,
    et: () => import("./terms.et.json") as Promise<{ default: LegalDocumentContent }>,
    fr: () => import("./terms.fr.json") as Promise<{ default: LegalDocumentContent }>,
    it: () => import("./terms.it.json") as Promise<{ default: LegalDocumentContent }>,
    pt: () => import("./terms.pt.json") as Promise<{ default: LegalDocumentContent }>,
  },
  dpa: {
    en: () => import("./dpa.en.json") as Promise<{ default: LegalDocumentContent }>,
    de: () => import("./dpa.de.json") as Promise<{ default: LegalDocumentContent }>,
    es: () => import("./dpa.es.json") as Promise<{ default: LegalDocumentContent }>,
    et: () => import("./dpa.et.json") as Promise<{ default: LegalDocumentContent }>,
    fr: () => import("./dpa.fr.json") as Promise<{ default: LegalDocumentContent }>,
    it: () => import("./dpa.it.json") as Promise<{ default: LegalDocumentContent }>,
    pt: () => import("./dpa.pt.json") as Promise<{ default: LegalDocumentContent }>,
  },
};

/** La langue demandee est-elle une de celles ou le document existe ? */
export const isDocumentLanguage = (language: string): language is DocumentLanguage =>
  (DOCUMENT_LANGUAGES as readonly string[]).includes(language);

export interface LoadedLegalContent {
  content: LegalDocumentContent;
  /** Langue reellement servie, qui peut differer de celle demandee. */
  language: DocumentLanguage;
  /** Vrai quand la traduction manquait et que l'anglais est servi a la place. */
  fellBackToEnglish: boolean;
}

/**
 * Charge le document dans la langue demandee, avec repli sur l'anglais.
 *
 * Le repli couvre la langue d'interface non traduite (le SPA parle 6 langues,
 * les documents 7, et rien ne garantit que les deux listes resteront alignees).
 * L'anglais prevaut juridiquement de toute facon, c'est donc le bon defaut.
 */
export const loadLegalContent = async (
  document: LegalDocumentId,
  language?: string
): Promise<LoadedLegalContent> => {
  const resolved = resolveDocumentLanguage(language);
  const requested: DocumentLanguage = isDocumentLanguage(resolved)
    ? resolved
    : CONTROLLING_LANGUAGE;

  const module = await LOADERS[document][requested]();

  return {
    content: module.default,
    language: requested,
    fellBackToEnglish:
      requested === CONTROLLING_LANGUAGE &&
      resolveDocumentLanguage(language) !== CONTROLLING_LANGUAGE,
  };
};
